import { useMediaQuery, useTheme, Box, Container, Grid, Paper, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from 'types/store'
import type { TeamData } from 'types/fpl/teams'
import { getLeagueAll, getGwUserPicks, gwPicksToMultiplierMap, isLeagueRequestCanceled } from 'api/league'
import type { InterestScore, LeagueTemplate, TemplatinessStats } from 'api/league'
import type { Captain, PlayerSummary } from 'types/fpl/elements'
import type { TeamSummary } from 'types/fpl/teams'
import type { Chip } from 'types/fpl/chips'
import type { LeagueTransferStats } from 'types/fpl/transfers'
import CaptainsGrid from 'components/structure/CaptainsGrid'
import LeagueTemplateCard from 'components/structure/LeagueTemplateCard'
import ChipsGrid from 'components/structure/ChipsGrid'
import TransfersSummary from 'components/structure/TransfersSummary'
import InterestScoreCard from 'components/structure/InterestScoreCard'
import SidebarPanel from 'components/structure/SidebarPanel'
import RightPanelOverlay from 'components/structure/RightPanelOverlay'
import type { ClassicLeague } from '../../types/fpl/leagues'
import { buildCacheKey, getCachedInsights, setCachedInsights, type CachedInsights } from 'utils/insightsCache'
import { resolveLeagueTeamCount } from 'utils/leagueTeamCount'


const Home: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const userTeamData: TeamData | null = useSelector((state: RootState) => state.user.teamData)

  const leagues: ClassicLeague[] =
    userTeamData?.leagues.classic
      ?.filter((l: any): boolean => l.league_type === 'x')
      .sort((a: any, b: any) => a.name.localeCompare(b.name)) ?? []

  const gws: number[] = [...(userTeamData?.entered_events ?? [])].sort((a: number, b: number) => b - a)

  const [selectedLeague, setSelectedLeague] = useState<ClassicLeague | null>(leagues.length > 0 ? leagues[0] as ClassicLeague : null)
  const [selectedGW, setSelectedGW] = useState<string>(gws.length > 0 ? gws[0].toString() : '')
  const [excludedPlayers, setExcludedPlayers] = useState<PlayerSummary[]>([])
  const [excludedTeams, setExcludedTeams] = useState<TeamSummary[]>([])

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [captains, setCaptains] = useState<Captain[]>([])
  const [chips, setChips] = useState<Chip[]>([])
  const [transferStats, setTransferStats] = useState<LeagueTransferStats | null>(null)
  const [interest, setInterest] = useState<InterestScore | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [fromCache, setFromCache] = useState<boolean>(false)
  const [captainMultiplierTotal, setCaptainMultiplierTotal] = useState(0)
  const [userPickMultipliers, setUserPickMultipliers] = useState<Record<number, number>>({})
  const [viewerInLeague, setViewerInLeague] = useState(false)
  const [leagueTemplate, setLeagueTemplate] = useState<LeagueTemplate | null>(null)
  const [templatinessStats, setTemplatinessStats] = useState<TemplatinessStats | null>(null)
  const [leagueTotalTeams, setLeagueTotalTeams] = useState(0)

  const fetchGenerationRef = React.useRef(0)
  const abortControllerRef = React.useRef<AbortController | null>(null)

  const cancelInFlightFetch = React.useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    fetchGenerationRef.current += 1
    setLoading(false)
  }, [])

  React.useEffect(() => () => cancelInFlightFetch(), [cancelInFlightFetch])

  const applyCachedInsights = React.useCallback((cached: CachedInsights) => {
    setCaptains(cached.captains)
    setChips(cached.chips)
    setTransferStats(cached.transfers)
    setInterest(cached.interest ?? null)
    setLastUpdated(cached.timestamp)
    setCaptainMultiplierTotal(cached.captain_multiplier_total ?? 0)
    setViewerInLeague(Boolean(cached.viewer_in_league))
    setLeagueTemplate(cached.league_template ?? null)
    setTemplatinessStats(cached.templatiness ?? null)
    setLeagueTotalTeams(cached.total_teams ?? resolveLeagueTeamCount(0, cached.captains))
    if (Array.isArray(cached.userGwPicks)) {
      setUserPickMultipliers(gwPicksToMultiplierMap(cached.userGwPicks))
    } else if (cached.userPickElementIds?.length) {
      setUserPickMultipliers(
        gwPicksToMultiplierMap(cached.userPickElementIds.map((id) => ({ element: id, multiplier: 1 }))),
      )
    } else {
      setUserPickMultipliers({})
    }
  }, [])

  useEffect(() => {
    cancelInFlightFetch()

    if (!selectedLeague || !selectedGW || !userTeamData) {
      setCaptains([])
      setChips([])
      setTransferStats(null)
      setError('')
      setLastUpdated(null)
      setFromCache(false)
      setCaptainMultiplierTotal(0)
      setUserPickMultipliers({})
      setViewerInLeague(false)
      setLeagueTemplate(null)
      setTemplatinessStats(null)
      setLeagueTotalTeams(0)
      return
    }
    const excludedIds = excludedPlayers.map(p => p.id)
    const excludedTeamIds = excludedTeams.map(t => t.id)
    const cacheKey = buildCacheKey(userTeamData.id, Number(selectedLeague.id), Number(selectedGW), excludedIds, excludedTeamIds)
    const cached = getCachedInsights(cacheKey)
    if (cached) {
      applyCachedInsights(cached)
      setFromCache(true)
      setError('')
      if (!Array.isArray(cached.userGwPicks)) {
        getGwUserPicks(userTeamData.id, Number(selectedGW)).then((rows) => {
          setUserPickMultipliers(gwPicksToMultiplierMap(rows))
          const { timestamp: _ts, ...rest } = cached
          setCachedInsights(cacheKey, { ...rest, userGwPicks: rows })
        })
      }
    } else {
      setCaptains([])
      setChips([])
      setTransferStats(null)
      setInterest(null)
      setError('')
      setLastUpdated(null)
      setFromCache(false)
      setCaptainMultiplierTotal(0)
      setUserPickMultipliers({})
      setViewerInLeague(false)
      setLeagueTemplate(null)
      setTemplatinessStats(null)
      setLeagueTotalTeams(0)
    }
  }, [selectedLeague?.id, selectedGW, userTeamData, applyCachedInsights, excludedPlayers, excludedTeams, cancelInFlightFetch])

  const handleCalculate = async (forceRefresh = false) => {
    if (!selectedLeague || !selectedGW || !userTeamData) return

    const leagueId: number = Number(selectedLeague.id)
    const gwId: number = Number(selectedGW)
    const excludedIds: number[] = excludedPlayers.map(p => p.id)
    const excludedTeamIds: number[] = excludedTeams.map(t => t.id)
    const cacheKey = buildCacheKey(userTeamData.id, leagueId, gwId, excludedIds, excludedTeamIds)

    if (!forceRefresh) {
      const cached = getCachedInsights(cacheKey)
      if (cached) {
        cancelInFlightFetch()
        applyCachedInsights(cached)
        setFromCache(true)
        setError('')
        if (!Array.isArray(cached.userGwPicks)) {
          getGwUserPicks(userTeamData.id, gwId).then((rows) => {
            setUserPickMultipliers(gwPicksToMultiplierMap(rows))
            const { timestamp: _ts, ...rest } = cached
            setCachedInsights(cacheKey, { ...rest, userGwPicks: rows })
          })
        }
        return
      }
    }

    cancelInFlightFetch()
    const controller = new AbortController()
    abortControllerRef.current = controller
    const generation = fetchGenerationRef.current

    setLoading(true)
    setError('')
    setFromCache(false)
    try {
      const [result, userPicks] = await Promise.all([
        getLeagueAll(leagueId, gwId, excludedIds, excludedTeamIds, userTeamData.id, controller.signal),
        getGwUserPicks(userTeamData.id, gwId, controller.signal),
      ])

      if (generation !== fetchGenerationRef.current) return

      setCaptains(result.captains)
      setChips(result.chips)
      setTransferStats(result.transfers)
      setInterest(result.interest ?? null)
      setCaptainMultiplierTotal(result.captain_multiplier_total ?? 0)
      setViewerInLeague(Boolean(result.viewer_in_league))
      setLeagueTemplate(result.league_template ?? null)
      setTemplatinessStats(result.templatiness ?? null)
      setLeagueTotalTeams(result.total_teams ?? resolveLeagueTeamCount(0, result.captains))
      setUserPickMultipliers(gwPicksToMultiplierMap(userPicks))
      setCachedInsights(cacheKey, { ...result, userGwPicks: userPicks })
      setLastUpdated(Date.now())
    } catch (e: unknown) {
      if (isLeagueRequestCanceled(e) || generation !== fetchGenerationRef.current) return
      const message = e instanceof Error ? e.message : 'Failed to fetch'
      setError(message)
      setCaptains([])
      setChips([])
      setTransferStats(null)
      setInterest(null)
      setLastUpdated(null)
      setCaptainMultiplierTotal(0)
      setUserPickMultipliers({})
      setViewerInLeague(false)
      setLeagueTemplate(null)
      setTemplatinessStats(null)
      setLeagueTotalTeams(0)
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false)
        abortControllerRef.current = null
      }
    }
  }

  const effectiveTotalTeams = resolveLeagueTeamCount(
    (leagueTotalTeams || selectedLeague?.rank_count) ?? 0,
    captains,
  )

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 2, sm: 3 },
        px: { xs: 1, sm: 2 },
        pb: { xs: 2, sm: 4 }
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 2 }}>
        <Grid item xs={12} sm={3}>
          <SidebarPanel
            userTeamData={userTeamData}
            leagues={leagues}
            gws={gws}
            selectedLeague={selectedLeague}
            setSelectedLeague={setSelectedLeague}
            selectedGW={selectedGW}
            setSelectedGW={setSelectedGW}
            excludedPlayers={excludedPlayers}
            setExcludedPlayers={setExcludedPlayers}
            excludedTeams={excludedTeams}
            setExcludedTeams={setExcludedTeams}
            onCalculate={handleCalculate}
            isMobile={isMobile}
            loading={loading}
            lastUpdated={lastUpdated}
            fromCache={fromCache}
          />
        </Grid>

        <Grid item xs={12} sm={9}>
          {(loading || captains.length === 0 || error) ? (
            <Paper sx={{ width: '100%' }}>
              <RightPanelOverlay loading={loading} hasData={captains.length > 0} error={error} />
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 2 }, flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
              {/* LEFT — captains + chips (matches export) */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, width: isMobile ? '100%' : 'auto' }}>
                <Paper sx={{ pt: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 }, pb: 0, minWidth: 0, width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant='h6'
                      sx={{ fontFamily: 'PremierLeagueBold', fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Captains
                    </Typography>
                    {selectedLeague?.name && selectedGW && (
                      <CaptainsGrid
                        captains={captains}
                        loading={false}
                        totalTeams={effectiveTotalTeams}
                        leagueName={selectedLeague.name}
                        gameweek={selectedGW}
                        chips={chips}
                        interest={interest}
                        leagueTemplate={leagueTemplate}
                        templatiness={templatinessStats}
                        transferStats={transferStats}
                        showHeaderButton={true}
                        captainMultiplierTotal={captainMultiplierTotal}
                        userPickMultipliers={userPickMultipliers}
                        viewerInLeague={viewerInLeague}
                      />
                    )}
                  </Box>
                  <CaptainsGrid
                    captains={captains}
                    loading={false}
                    totalTeams={effectiveTotalTeams}
                    leagueName={selectedLeague?.name}
                    gameweek={selectedGW}
                    chips={chips}
                    captainMultiplierTotal={captainMultiplierTotal}
                    userPickMultipliers={userPickMultipliers}
                    viewerInLeague={viewerInLeague}
                  />
                </Paper>

                <Paper sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }}>
                  <Typography variant='h6' fontFamily='PremierLeagueBold' sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 1 }}>
                    Chips
                  </Typography>
                  <ChipsGrid chips={chips} loading={false} totalTeams={effectiveTotalTeams} />
                </Paper>

                <Paper sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }}>
                  <Typography variant='h6' fontFamily='PremierLeagueBold' sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 1 }}>
                    Transfers
                  </Typography>
                  <TransfersSummary stats={transferStats} totalTeams={effectiveTotalTeams} />
                </Paper>
              </Box>

              {/* RIGHT — interest score + pitch (matches export) */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: isMobile ? '100%' : { sm: 340, md: 380, lg: 420 },
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }}>
                  <InterestScoreCard interest={interest} loading={loading} />
                </Paper>

                {leagueTemplate?.players?.length ? (
                  <Paper sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }}>
                    <LeagueTemplateCard
                      template={leagueTemplate}
                      templatiness={templatinessStats}
                      totalTeams={effectiveTotalTeams}
                      compact
                    />
                  </Paper>
                ) : null}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  )
}

export default Home
